import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Activity,
  Settings,
  BarChart3,
  Wallet,
  CheckCircle,
  Coins,
  Truck,
  ArrowRightLeft,
  ChevronLeft,
  ShoppingCart,
  Megaphone,
  Store,
  Trophy,
  Users,
  ArrowDownCircle,
  History,
  Boxes,
  ArrowUpCircle,
  Repeat,
  Warehouse,
  Globe,
  ShieldCheck,
  Tag,      
  Package,  
  Target,        
  ClipboardList, 
} from "lucide-react";
import { NavLink } from "react-router-dom";

export default function Sidebar() {
  const userRole = localStorage.getItem("role_user"); 
  
  const getLevelNumber = (role) => {
    if (role === "asthead") return 3;
    if (role === "head") return 2;
    if (role === "manager") return 1;
    return 0; 
  };

  const userLevel = getLevelNumber(userRole);
  const isStoreLeader = userRole === "store_leader";

  const getInitialSidebar = () => {
    if (typeof window === "undefined") return false;
    const saved = localStorage.getItem("sidebar");
    if (saved !== null) return JSON.parse(saved);
    return false; 
  };

  const [open, setOpen] = useState(getInitialSidebar);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    localStorage.setItem("sidebar", JSON.stringify(open));
  }, [open]);

  useEffect(() => {
    const check = () => {
      const mobileStatus = window.innerWidth < 768;
      setIsMobile(mobileStatus);
      // 💡 Jika layar berubah jadi mobile, otomatis tutup sidebar
      if (mobileStatus) {
        setOpen(false);
      }
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check); 
  }, []);

  const toggleSidebar = () => setOpen((p) => !p);

  // Fungsi untuk menutup sidebar saat menu diklik (hanya jika sedang terbuka)
  const handleMenuClick = () => {
    if (open) setOpen(false);
  };

  const menu = [
    { divider: "Top Level Panel", minLevel: 1 },
    { divider: "Store Level Panel", storeOnly: true },
    { name: "Dashboard HQ", icon: LayoutDashboard, path: "/dashboard", minLevel: 1 },
    { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard", storeOnly: true },
    { name: "Traffic", icon: Activity, path: "/traffic", minLevel: 1 },
    { name: "Traffic", icon: Activity, path: "/traffic", storeOnly: true },
    
    { name: "Action Plan", icon: Target, path: "/marketing/marketing-plan", minLevel: 1 },
    { name: "Marketing", icon: Megaphone, path: "/marketing/marketing-hq", minLevel: 1 },
    { name: "Marketing Plan", icon: Megaphone, path: "/marketing/marketing-plan", storeOnly: true }, 

    { name: "Tiktok Shop", icon: ShoppingCart, path: "/tiktokshop/overview", minLevel: 1 },
    { name: "Promotions", icon: Tag, path: "/marketing/promotions", minLevel: 1 },
    { name: "Product", icon: Package, path: "/inventory/stock-card", minLevel: 1 },

    { divider: "Sales & Order Reports", storeOnly: true },
    { name: "Laporan Penjualan Harian", icon: BarChart3, path: "/sales/daily-report", storeOnly: true },
    { name: "Achievement Sales", icon: Trophy, path: "/sales/achievement", storeOnly: true },
    { name: "Order Tiktokshop", icon: ShoppingCart, path: "/tiktokshop/orders", badge: 1, storeOnly: true },
    { name: "Order Webstore", icon: Store, path: "/webstore/orders", badge: 1, storeOnly: true },

    { divider: "Finance", storeOnly: true },
    { name: "Validasi Omset", icon: CheckCircle, path: "/finance/validate-payment", storeOnly: true },
    { name: "Modal", icon: Coins, path: "/finance/modal", storeOnly: true },
    { name: "Tiktok Finance", icon: CheckCircle, path: "/finance/tiktok-finance", storeOnly: true },
    { name: "Pettycash", icon: Wallet, path: "/finance/pettycash", storeOnly: true },
    { name: "Pickup Omset", icon: Truck, path: "/finance/pickup-omset", storeOnly: true },
    { name: "Transfer Masuk", icon: ArrowRightLeft, path: "/finance/transfer", storeOnly: true },
    { name: "Perintah Transfer", icon: ArrowRightLeft, path: "/finance/perintah-transfer", storeOnly: true },

    { divider: "Warehouse", storeOnly: true },
    { name: "Surat Jalan Masuk", icon: ArrowDownCircle, path: "/warehouse/surat-jalan-masuk", badge: 1, storeOnly: true },
    { name: "Mutasi Masuk", icon: ArrowDownCircle, path: "/warehouse/mutasi-masuk", badge: 1, storeOnly: true },
    { name: "Receive Direct", icon: ArrowDownCircle, path: "/warehouse/receive-direct", badge: 1, storeOnly: true },
    { name: "Retur Marketing", icon: Repeat, path: "/warehouse/retur-marketing", storeOnly: true },
    { name: "Retur Promosi", icon: Repeat, path: "/warehouse/retur-promosi", badge: 1, storeOnly: true }, 
    { name: "Retur Warehouse", icon: Repeat, path: "/warehouse/retur-center", badge: 1, storeOnly: true },
    { name: "Instruksi Mutasi", icon: ArrowUpCircle, path: "/warehouse/perintah-mutasi", badge: 1, storeOnly: true },
    { name: "Stok Gudang", icon: Warehouse, path: "/inventory/stock-card", storeOnly: true },
    { name: "Stok Gudang Tiktok", icon: Boxes, path: "/warehouse/gudang-tiktok", storeOnly: true },
    { name: "Log Update Stok Tiktok", icon: History, path: "/warehouse/log-auto-update-tiktok", storeOnly: true },
    { name: "Stok Nasional", icon: Globe, path: "/inventory/national-stock", storeOnly: true },
    { name: "Mitigasi Audit", icon: ShieldCheck, path: "/warehouse/mitigasi-audit", storeOnly: true },

    { divider: "Warehouse Tiktok", storeOnly: true },
    { name: "Sales Order Req", icon: ArrowUpCircle, path: "/warehouse/sales-order-req", badge: 1, storeOnly: true },
    { name: "Pembatalan & Retur", icon: Warehouse, path: "/tiktokshop/cancel-orders", storeOnly: true },
    { name: "Stok Gudang Tiktok", icon: Warehouse, path: "/warehouse/stok-gudang", storeOnly: true },

    { divider: "System" },
    { name: "Settings", icon: Settings, path: "/settings" },
    { name: "Crew", icon: Users, path: "/setting/crew" },
  ];

  if (isMobile) return <MobileBottomNav />;

  return (
    <motion.div
      animate={{ width: open ? 260 : 80 }}
      transition={{ type: "spring", stiffness: 260, damping: 25 }}
      className="h-screen bg-gray-50 border-r flex flex-col shrink-0 sticky top-0"
    >
      {/* HEADER LOGO */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <img
          src="https://finance.biensicore.com/assets/logo-7c7ad582.png"
          className={`object-contain transition-all ${open ? "h-8" : "h-8 w-8"}`}
        />
        <button onClick={toggleSidebar} className="p-2 rounded-lg hover:bg-gray-100">
          {open ? <ChevronLeft size={18} /> : "☰"}
        </button>
      </div>

      {/* RENDER MENU BERDASARKAN FILTER */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {menu.map((item, i) => {
          if (item.minLevel && userLevel < item.minLevel) return null;
          if (item.storeOnly && !isStoreLeader) return null;

          if (item.divider) {
            return open && (
              <p key={i} className="text-xs text-gray-400 mt-4 px-3">
                {item.divider}
              </p>
            );
          }

          const Icon = item.icon;

          return (
            <div key={i} className="relative group">
              <NavLink
                to={item.path}
                onClick={handleMenuClick} // 👈 Ditambahkan di sini agar menutup otomatis saat diklik
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-xl transition relative
                  ${isActive ? "bg-gray-200 text-black font-semibold" : "text-gray-600 hover:bg-gray-100"}`
                }
              >
                <div className="relative flex items-center justify-center">
                  <Icon size={20} />
                  {item.badge && !open && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white ring-2 ring-gray-50">
                      {item.badge}
                    </span>
                  )}
                </div>

                {open && <span className="text-sm flex-1">{item.name}</span>}
                
                {item.badge && open && (
                  <span className="text-[10px] bg-rose-500 text-white px-2 rounded-full font-medium">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* =========================
    📱 MOBILE BOTTOM NAV
========================= */
function MobileBottomNav() {
  const nav = [
    { name: "Home", icon: LayoutDashboard, path: "/m-home" },
    { name: "Traffic", icon: Activity, path: "/traffic" },
    { name: "Finance", icon: Wallet, path: "/finance/modal" },
    { name: "Warehouse", icon: Warehouse, path: "/warehouse/stok-gudang" },
    { name: "Settings", icon: Settings, path: "/settings" },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-md flex justify-around py-2">
      {nav.map((item, i) => {
        const Icon = item.icon;

        return (
          <NavLink
            key={i}
            to={item.path}
            className={({ isActive }) =>
              `flex flex-col items-center text-xs ${
                isActive ? "text-black font-semibold" : "text-gray-500"
              }`
            }
          >
            <Icon size={20} />
            <span className="mt-1">{item.name}</span>
          </NavLink>
        );
      })}
    </div>
  );
}